import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import {
  useInboundPackageByIdQuery,
  useLazyGetDeliveryNoteQuery,
  useGenerateLabelMutation,
  useCheckTaskStatusQuery,
  useLazyGetGeneratedLabelQuery,
} from 'api/warehouse';
import { WAREHOUSE_ROUTES } from 'constants/routes';

interface Dimensions {
  height: string;
  width: string;
  length: string;
  weight: string;
  lengthUnit: 'cm' | 'ft/in';
  weightUnit: 'g' | 'kg';
}

export const useFullPackageAndDimensions = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPackageIndex, _setCurrentPackageIndex] = useState(0);
  const [dimensions, setDimensions] = useState<Dimensions>({
    height: '',
    width: '',
    length: '',
    weight: '',
    lengthUnit: 'cm',
    weightUnit: 'g',
  });

  const [getDeliveryNote, { isLoading: isDownloadingNote }] = useLazyGetDeliveryNoteQuery();
  const [generateLabel, { isLoading: isGeneratingLabel }] = useGenerateLabelMutation();
  const [getGeneratedLabel] = useLazyGetGeneratedLabelQuery();

  const [taskId, setTaskId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [labelGenerationStatus, setLabelGenerationStatus] = useState<'idle' | 'exw' | 'generating'>('idle');

  const packageInfo = location.state as
    | {
        fullPackageIds: string[];
        itemId: string;
        itemSku: string;
        packages?: any[];
      }
    | undefined;

  const {
    data: order,
    isLoading,
    error,
  } = useInboundPackageByIdQuery(id || '', {
    skip: !id,
  });

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [error, navigate, message]);

  useEffect(() => {
    if (!packageInfo) {
      message.warning('No package information found');
      navigate(`/warehouse/inbound/${id}`);
    }
  }, [packageInfo, id, navigate, message]);

  const currentPackageId = packageInfo?.fullPackageIds[currentPackageIndex];
  const currentPackage = packageInfo?.packages?.[currentPackageIndex];
  const currentPackageUuid = currentPackage?.orderNumber || currentPackageId;

  // Polling for task status
  const { data: taskStatusData } = useCheckTaskStatusQuery(taskId || '', {
    skip: !taskId || !isPolling,
    pollingInterval: 3000, // Poll every 3 seconds
  });

  // Handle polling completion
  useEffect(() => {
    const handleTaskCompletion = async () => {
      // Check if task is done (response format: { data: true/false })
      if (taskStatusData?.data === true && isPolling && currentPackageId) {
        setIsPolling(false);

        try {
          // Fetch the generated label
          const response: any = await getGeneratedLabel(currentPackageId).unwrap();

          if (response?.data) {
            // Navigate to label print page with label data
            message.success('Label generated successfully');
            navigate(`/warehouse/inbound/${id}/label-print`, {
              state: { labelData: response.data },
            });
          } else {
            throw new Error('Invalid label response');
          }

          setTaskId(null);
        } catch (error: any) {
          message.error(error?.message || 'Failed to download generated label');
          setLabelGenerationStatus('idle');
          setTaskId(null);
        }
      }
    };

    handleTaskCompletion();
  }, [taskStatusData, isPolling, currentPackageId, id, getGeneratedLabel, navigate, message]);

  const handleDimensionChange = useCallback((field: keyof Dimensions, value: string) => {
    setDimensions((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleUnitChange = useCallback((field: keyof Dimensions, value: string) => {
    setDimensions((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Validate that all dimensions are filled and greater than 0
  const areDimensionsValid =
    dimensions.height !== '' &&
    dimensions.width !== '' &&
    dimensions.length !== '' &&
    dimensions.weight !== '' &&
    parseFloat(dimensions.height) > 0 &&
    parseFloat(dimensions.width) > 0 &&
    parseFloat(dimensions.length) > 0 &&
    parseFloat(dimensions.weight) > 0;

  const handlePrintDeliveryNote = useCallback(async () => {
    if (!currentPackageId) {
      message.error('No package ID available');
      return;
    }

    try {
      const response = await getDeliveryNote(currentPackageId).unwrap();

      // Extract the ATTACHMENTS URL from the response
      const attachmentUrl = response?.data?.files?.ATTACHMENTS;

      if (!attachmentUrl) {
        message.error('Delivery note URL not found');
        return;
      }

      // Open the URL - browser will handle it (download or open)
      window.location.href = attachmentUrl;

      message.success('Delivery note download started');
    } catch (error: any) {
      message.error(error?.message || 'Failed to load delivery note');
    }
  }, [currentPackageId, getDeliveryNote, message]);

  const handleGenerateLabel = useCallback(async () => {
    // Validate dimensions before proceeding
    if (!areDimensionsValid) {
      message.error('Please fill in all dimensions with values greater than 0');
      return;
    }

    if (!currentPackageId) {
      message.error('No package ID available');
      return;
    }

    try {
      message.loading({ content: 'Generating label...', key: 'generate-label' });

      // Convert units to backend format
      const massUnit = dimensions.weightUnit === 'kg' ? 'KG' : 'G';
      const lengthMetric = dimensions.lengthUnit === 'ft/in' ? 'FT' : 'CM';

      const response: any = await generateLabel({
        orderId: currentPackageId,
        height: parseFloat(dimensions.height),
        width: parseFloat(dimensions.width),
        length: parseFloat(dimensions.length),
        weight: parseFloat(dimensions.weight),
        massUnit,
        lengthMetric,
      }).unwrap();

      message.destroy('generate-label');

      if (response?.data?.deliveryTerms === 'EXW') {
        // Show "Label Request is sent to the Client" page
        setLabelGenerationStatus('exw');
      } else if (response?.data?.taskId) {
        // Start polling for task completion
        setTaskId(response.data.taskId);
        setIsPolling(true);
        setLabelGenerationStatus('generating');
      } else {
        message.error('Invalid response from server');
      }
    } catch (error: any) {
      message.error({
        content: error?.message || 'Failed to generate label',
        key: 'generate-label',
      });
    }
  }, [areDimensionsValid, currentPackageId, dimensions, generateLabel, message]);

  const handleContinue = useCallback(() => {
    // Navigate back to order details after completing EXW flow
    setLabelGenerationStatus('idle');
    navigate(`/warehouse/inbound/${id}`);
  }, [id, navigate]);

  const completedCount =
    order?.itemsResponseList?.filter((item: any) => item.status === 'CHECKED' || item.status === 'CLAIMED').length || 0;

  const handleBack = useCallback(() => {
    // Go back to packaging page (previous step)
    // Don't pass packages - they will be loaded from order details API
    navigate(WAREHOUSE_ROUTES.packaging.replace(':id', id || ''), {
      state: {
        itemId: packageInfo?.itemId,
        sku: packageInfo?.itemSku,
      },
    });
  }, [id, navigate, packageInfo]);

  const totalCount = order?.itemsResponseList?.length || 0;

  return {
    isLoading,
    currentPackageId,
    currentPackageUuid,
    currentPackageIndex,
    totalPackages: packageInfo?.fullPackageIds.length || 0,
    dimensions,
    areDimensionsValid,
    isDownloadingNote,
    isGeneratingLabel,
    labelGenerationStatus,
    handleDimensionChange,
    handleUnitChange,
    handlePrintDeliveryNote,
    handleGenerateLabel,
    handleContinue,
    handleBack,
    completedCount,
    totalCount,
  };
};
